import { Koi } from '@ksyos/koi';
import { ServiceCode } from '../../enums/serviceCode';
import { ServiceType } from '../../enums/serviceType';
import { SpirometerSoftwareNames } from '../../enums/spirometerSoftwareNames';
export interface IAddCarePathServiceConsumerSettingsValidated extends IServiceConsumerSettingsValidated {
    isDisabled: boolean;
    profileId: string;
}
export interface IServiceConsumerSettingsValidated {
    addAutoSubsequentServices: boolean;
    autoSubsequentService?: IAutoSubsequentServiceValidated | null;
    followUpMeasurementGgz?: boolean;
    id: string;
    isAutoCloseRequest: boolean;
    isGraderSelector?: boolean;
    isRequestCreator: boolean;
    sendNotificationEmployment: boolean;
    sendNotificationPractitioner: boolean;
    sendNotificationPractitionerWhenDeviant?: boolean;
    sendZorgMailOnExaminationRequest?: boolean;
    sendZorgMailOnReferralIntake?: boolean;
    sendZorgMailOnReferralRequest?: boolean;
    service: IServiceValidated;
    spirometerSoftwareName?: SpirometerSoftwareNames;
    subsequentServices: ISubsequentServiceValidated[];
    usingRestEcgMeter?: boolean;
    usingSpirometer?: boolean;
    weeksForFollowUpMeasurementGgz?: number;
}

export interface ISubsequentServiceValidated {
    succeedingService: IServiceValidated;
    succeedingServiceId: string;
}

export interface IAutoSubsequentServiceValidated {
    isDeviation: boolean;
    providerOrganization: {
        id: string;
        isOwnOrganizationRequestRecipient?: boolean;
        isRequestRecipient?: boolean;
        name: string;
    };
    providerOrganizationId: string | undefined;
    providerPractitionerId: string | undefined;
    subsequentServiceId: string | undefined;
    succeedingService: IServiceValidated;
}

export interface IServiceValidated {
    code: ServiceCode;
    id: string;
    name: string;
    serviceType: ServiceType;
}

export const serviceSchema = Koi.object().keys({
    code: Koi.koi().enum(ServiceCode).required(),
    id: Koi.string().uuid(),
    name: Koi.string(),
    serviceType: Koi.koi().enum(ServiceType).required(),
});

export const autoSubsequentServiceSchema = Koi.object().keys({
    isDeviation: Koi.boolean().required(),
    providerOrganization: Koi.when('providerOrganizationId', {
        is: Koi.string().uuid(),
        otherwise: Koi.optional(),
        then: Koi.object().keys({
            id: Koi.string().uuid().required(),
            isOwnOrganizationRequestRecipient: Koi.boolean().optional(),
            isRequestRecipient: Koi.boolean().optional(),
            name: Koi.string().max(255),
        }),
    }),
    providerOrganizationId: Koi.when('subsequentServiceId', {
        is: Koi.string().uuid(),
        otherwise: Koi.valid(null).optional(),
        then: Koi.string().uuid().required(),
    }),
    providerPractitionerId: Koi.when('providerOrganization.isOwnOrganizationRequestRecipient', {
        is: true,
        otherwise: Koi.when('providerOrganization.isRequestRecipient', {
            is: true,
            otherwise: Koi.when('succeedingService.serviceType', {
                is: [ServiceType.referral, ServiceType.referralSmall],
                otherwise: Koi.when('providerOrganizationId', {
                    is: Koi.string().uuid(),
                    otherwise: Koi.valid(null).optional(),
                    then: Koi.string().uuid().required(),
                }),
                then: Koi.valid(null),
            }),
            then: Koi.string().uuid().allow(null),
        }),
        then: Koi.string().uuid().allow(null),
    }),
    subsequentServiceId: Koi.string().uuid().required(),
    succeedingService: Koi.when('providerOrganizationId', {
        is: Koi.string().uuid(),
        otherwise: Koi.optional(),
        then: serviceSchema,
    }),
});

export const serviceConsumerSettingsAdd = Koi.object()
    .keys({
        addAutoSubsequentServices: Koi.boolean().optional(),
        autoSubsequentService: Koi.when('addAutoSubsequentServices', {
            is: true,
            otherwise: Koi.koi().optional(),
            then: autoSubsequentServiceSchema,
        }),
        followUpMeasurementGgz: Koi.when('service.serviceType', {
            is: ServiceType.examination,
            otherwise: Koi.valid(false),
            then: Koi.when('service.code', {
                is: Koi.allow(ServiceCode.GGZOembl, ServiceCode.GGZOint),
                otherwise: Koi.valid(false),
                then: Koi.boolean().required(),
            }),
        }),
        id: Koi.string().uuid(),
        isAutoCloseRequest: Koi.when('service.serviceType', {
            is: Koi.allow(ServiceType.examination, ServiceType.consultation, ServiceType.referral),
            otherwise: Koi.valid(false),
            then: Koi.boolean().required(),
        }),
        isGraderSelector: Koi.when('service.serviceType', {
            is: ServiceType.examination,
            otherwise: Koi.valid(false),
            then: Koi.boolean().required(),
        }),
        isRequestCreator: Koi.when('service.serviceType', {
            is: Koi.allow(ServiceType.examination, ServiceType.consultation, ServiceType.referral),
            otherwise: Koi.valid(false),
            then: Koi.boolean().required(),
        }),
        sendNotificationEmployment: Koi.when('service.serviceType', {
            is: Koi.allow(ServiceType.examination, ServiceType.consultation, ServiceType.referral),
            otherwise: Koi.valid(false),
            then: Koi.boolean().required(),
        }),
        sendNotificationPractitioner: Koi.when('service.serviceType', {
            is: Koi.allow(ServiceType.examination, ServiceType.consultation, ServiceType.referral),
            otherwise: Koi.valid(false),
            then: Koi.boolean().required(),
        }),
        sendNotificationPractitionerWhenDeviant: Koi.when('service.serviceType', {
            is: ServiceType.examination,
            otherwise: Koi.valid(false),
            then: Koi.boolean().required(),
        }),
        sendZorgMailOnExaminationRequest: Koi.when('service.serviceType', {
            is: ServiceType.examination,
            otherwise: Koi.valid(false),
            then: Koi.boolean().required(),
        }),
        sendZorgMailOnReferralIntake: Koi.when('service.serviceType', {
            is: ServiceType.referral,
            otherwise: Koi.valid(false),
            then: Koi.boolean().required(),
        }),
        sendZorgMailOnReferralRequest: Koi.when('service.serviceType', {
            is: ServiceType.referral,
            otherwise: Koi.valid(false),
            then: Koi.boolean().required(),
        }),
        service: serviceSchema,
        spirometerSoftwareName: Koi.koi()
            .enum(SpirometerSoftwareNames)
            .when('usingSpirometer', { is: true, otherwise: Koi.allow(null), then: Koi.disallow(null) }),
        usingRestEcgMeter: Koi.when('service.serviceType', {
            is: ServiceType.consultation,
            then: Koi.when('service.code', {
                is: Koi.allow(ServiceCode.TCOR, ServiceCode.TCCi, ServiceCode.TCCh),
                otherwise: Koi.valid(false),
                then: Koi.boolean().required(),
            }),
        }),
        usingSpirometer: Koi.when('service.serviceType', {
            is: ServiceType.consultation,
            then: Koi.when('service.code', {
                is: ServiceCode.TPC,
                otherwise: Koi.valid(false),
                then: Koi.boolean().required(),
            }),
        }),
        weeksForFollowUpMeasurementGgz: Koi.when('followUpMeasurementGgz', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.number().min(1).required(),
        }),
    })
    .options({ convert: false });

export const addCarePathServiceConsumerSettingsAdd = serviceConsumerSettingsAdd.concat(
    Koi.object().keys({
        isDisabled: Koi.boolean(),
        profileId: Koi.string().uuid(),
    })
);
